import { useState, useEffect, useRef } from "react";
// import { Pagination, PaginationItem, PaginationLink } from "reactstrap";
import { BiSort } from "react-icons/bi";
import { IoFilter } from "react-icons/io5";
import {
  FcAlphabeticalSortingAz,
  FcAlphabeticalSortingZa,
} from "react-icons/fc";
import Pagination from "./pagination";
import Button from "./button";
import DateFilter from "./dateFilter";

const Table = ({
  pageTitle,
  headings,
  data,
  currentPage,
  totalPages,
  onPageChange,
  enablePagination,
  enableSearch,
  className,
  addBtnText,
  addCallback,
  addBulkText,
  addBulkCallback,
  filtersDropdown,
  editBtnText,
  editCallback,
  isDropdownFilterOpen,
  setIsDropdownFilterOpen,
  card,
}) => {
  const dropdownRef = useRef(null);
  const [sortedData, setSortedData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isDateFilterOpen, setIsDateFilterOpen] = useState(false);
  const [showDateFilter, setShowDateFilter] = useState(false);

  useEffect(() => {
    setSortedData(data);
    resetFilters();
  }, [data]);

  useEffect(() => {
    if (filters.length > 0) {
      const filteredData = applyFilters(data, filters);
      setSortedData(filteredData);
    } else {
      setSortedData(data);
    }
  }, [filters, data]);

  useEffect(() => {
    if (searchQuery === "") {
      setSortedData(data);
    }
  }, [searchQuery]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const resetFilters = () => {
    const tableFilters = [];
    headings?.forEach((header) => {
      if (header?.dateFilter) {
        setShowDateFilter(true);
      }
      if (header.filter) {
        const uniqueOptions = [
          ...new Set(data?.map((row) => row[header?.fieldName])),
        ];
        const filter = {
          name: header?.name,
          field: header?.fieldName,
          options: uniqueOptions?.map((option) => ({
            name: option,
            selected: false,
          })),
        };
        tableFilters.push(filter);
      }
    });
    setFilters(tableFilters);
  };

  const handleSort = (fieldName) => {
    const header = headings?.find((h) => h?.fieldName === fieldName);

    if (!header) return;

    if (header?.sortKey === "none") {
      header.sortKey = "asc";
    } else if (header?.sortKey === "asc") {
      header.sortKey = "desc";
    } else {
      header.sortKey = "none";
    }

    let sortedArray = [...data];
    if (header?.sortKey === "asc") {
      sortedArray.sort((a, b) => (a[fieldName] > b[fieldName] ? 1 : -1));
    } else if (header?.sortKey === "desc") {
      sortedArray.sort((a, b) => (a[fieldName] < b[fieldName] ? 1 : -1));
    } else {
      sortedArray = [...data];
    }

    setSortedData(sortedArray);
  };

  const handleFilterChange = (e, filter, option) => {
    let newFilters = [...filters];

    newFilters = newFilters.map((newFilter) => {
      if (newFilter?.name === filter?.name) {
        let changedFilter = { ...newFilter };
        changedFilter.options = changedFilter?.options?.map((opt) => {
          let changedOption = { ...opt };
          if (changedOption?.name === option?.name) {
            changedOption.selected = !changedOption?.selected;
          }
          return changedOption;
        });

        return changedFilter;
      }
      return newFilter;
    });

    setFilters(newFilters);
  };

  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
    const filtered = data?.filter((rowData) =>
      Object.values(rowData).some(
        (colData) =>
          typeof colData === "string" &&
          colData?.toLowerCase().includes(e.target.value.toLowerCase())
      )
    );
    setSortedData(filtered);
  };

  const applyFilters = (data, filters) => {
    if (!filters || filters?.length === 0) return data;

    return filters.reduce((filteredData, filter) => {
      const selectedOptions = filter?.options?.filter(
        (option) => option?.selected
      );

      if (selectedOptions?.length === 0) return filteredData;

      return filteredData.filter((item) =>
        selectedOptions.some((option) => item[filter?.field] === option?.name)
      );
    }, data);
  };

  const handleFilterDate = (filteredDataByDate) => {
    setSortedData(filteredDataByDate);
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center gap-3 mb-2">
        <h3>{pageTitle}</h3>
        <div className="d-flex gap-3">
          {enableSearch && (
            <input
              className="form-control w-auto"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => handleSearch(e)}
            />
          )}
          {filters?.length > 0 && (
            <div className="position-relative">
              <Button
                text={
                  <span>
                    <IoFilter /> Filters
                  </span>
                }
                callBack={() => setIsFilterOpen(!isFilterOpen)}
              />
              {isFilterOpen && (
                <div
                  className="filter-btn card position-absolute end-0 z-50 shadow"
                  style={{ top: "2.5rem", zIndex: "1" }}
                >
                  <div className="card-body">
                    {filters?.map((filter, i) => (
                      <div key={i}>
                        <h6>{filter?.name}</h6>
                        <div>
                          {filter?.options?.map?.((option, j) => (
                            <div key={j} className="form-check">
                              <label className="form-check-label">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={option?.selected}
                                  onChange={(e) =>
                                    handleFilterChange(e, filter, option)
                                  }
                                />
                                {option?.name}
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          {addBulkText && (
            <Button text={addBulkText} callBack={addBulkCallback} />
          )}
          {addBtnText && <Button text={addBtnText} callBack={addCallback} />}
          {editBtnText && <Button text={editBtnText} callBack={editCallback} />}
          {filtersDropdown && (
            <div className="position-relative" ref={dropdownRef}>
              <Button
                text={
                  <span>
                    <IoFilter /> Filters
                  </span>
                }
                callBack={() => setIsDropdownFilterOpen((prev) => !prev)}
              />
              {isDropdownFilterOpen && (
                <div
                  className="filter-dropdown card position-absolute end-0 shadow-lg"
                  style={{ top: "2.5rem", width: "max-content", zIndex: "5" }}
                >
                  <div className="card-body">{filtersDropdown()}</div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <div
        className={`${card ? "card px-3 py-2" : ""} overflow-auto rounded-4`}
      >
        <table className="table">
          <thead>
            <tr>
              {headings?.map((header, index) => (
                <th
                  key={index}
                  style={{ cursor: header.isSortable ? "pointer" : "default" }}
                >
                  <div className="d-flex align-items-center justify-content-center gap-2">
                    {header?.name}
                    {header?.isSortable && (
                      <span
                        onClick={() =>
                          header?.isSortable && handleSort(header?.fieldName)
                        }
                      >
                        {header?.sortKey === "none" && <BiSort />}
                        {header?.sortKey === "asc" && (
                          <FcAlphabeticalSortingAz />
                        )}
                        {header?.sortKey === "desc" && (
                          <FcAlphabeticalSortingZa />
                        )}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedData?.length > 0 ? (
              sortedData?.map((row, rowIndex) => {
                if (typeof row !== "object" || row === null) return null; // Safety check
                return (
                  <tr key={rowIndex}>
                    {Object.entries(row).map(([key, cell], cellIndex) => {
                      if (key === "id") return null; // Skip "id"
                      return (
                        <td key={cellIndex} style={{ verticalAlign: "middle" }}>
                          {typeof cell === "function" ? cell() : cell}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={headings?.length}>No data available</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="d-flex justify-content-end mt-2">
        {enablePagination && (
          // <Pagination
          //   className="justify-content-center mt-3"
          //   style={{ gap: "5px" }}
          // >
          //   {/* Previous Button */}
          //   <PaginationItem disabled={currentPage === 0}>
          //     <PaginationLink
          //       previous
          //       onClick={() => onPageChange(currentPage - 1)}
          //       style={{
          //         color: "#34495e",
          //         backgroundColor: currentPage === 0 ? "#bdc3c7" : "white",
          //         borderRadius: "8px",
          //         padding: "8px 12px",
          //         width: "40px",
          //         textAlign: "center",
          //       }}
          //     >
          //       <FaArrowLeft />
          //     </PaginationLink>
          //   </PaginationItem>

          //   {totalPages <= 7 ? (
          //     [...Array(totalPages).keys()].map((pageNumber) => (
          //       <PaginationItem
          //         key={pageNumber}
          //         active={pageNumber === currentPage}
          //       >
          //         <PaginationLink
          //           onClick={() => onPageChange(pageNumber)}
          //           style={{
          //             backgroundColor:
          //               pageNumber === currentPage ? "#34495e" : "white",
          //             color: pageNumber === currentPage ? "white" : "#34495e",
          //             borderColor: "#34495e",
          //             borderRadius: "8px",
          //             padding: "8px 12px",
          //             width: "40px",
          //             textAlign: "center",
          //             boxShadow:
          //               pageNumber === currentPage
          //                 ? "0 2px 8px rgba(0,0,0,0.1)"
          //                 : "none",
          //           }}
          //         >
          //           {pageNumber + 1}
          //         </PaginationLink>
          //       </PaginationItem>
          //     ))
          //   ) : (
          //     <>
          //       {currentPage > 1 && (
          //         <>
          //           <PaginationItem>
          //             <PaginationLink
          //               onClick={() => onPageChange(0)}
          //               style={{
          //                 borderColor: "#34495e",
          //                 color: "#34495e",
          //                 borderRadius: "8px",
          //                 padding: "8px 12px",
          //                 width: "40px",
          //                 textAlign: "center",
          //               }}
          //             >
          //               1
          //             </PaginationLink>
          //           </PaginationItem>
          //           {currentPage > 2 && (
          //             <li
          //               style={{
          //                 display: "flex",
          //                 alignItems: "center",
          //                 justifyContent: "center",
          //                 fontSize: "18px",
          //                 color: "#34495e",
          //                 width: "40px",
          //                 height: "40px",
          //               }}
          //             >
          //               ...
          //             </li>
          //           )}
          //         </>
          //       )}

          //       {[...Array(totalPages).keys()]
          //         .filter(
          //           (pageNumber) =>
          //             pageNumber >= currentPage - 1 &&
          //             pageNumber <= currentPage + 1
          //         )
          //         .map((pageNumber) => (
          //           <PaginationItem
          //             key={pageNumber}
          //             active={pageNumber === currentPage}
          //           >
          //             <PaginationLink
          //               onClick={() => onPageChange(pageNumber)}
          //               style={{
          //                 backgroundColor:
          //                   pageNumber === currentPage ? "#34495e" : "white",
          //                 color:
          //                   pageNumber === currentPage ? "white" : "#34495e",
          //                 borderColor: "#34495e",
          //                 borderRadius: "8px",
          //                 padding: "8px 12px",
          //                 width: "40px",
          //                 textAlign: "center",
          //                 boxShadow:
          //                   pageNumber === currentPage
          //                     ? "0 2px 8px rgba(0,0,0,0.1)"
          //                     : "none",
          //               }}
          //             >
          //               {pageNumber + 1}
          //             </PaginationLink>
          //           </PaginationItem>
          //         ))}

          //       {currentPage < totalPages - 2 && (
          //         <>
          //           {currentPage < totalPages - 3 && (
          //             <li
          //               style={{
          //                 display: "flex",
          //                 alignItems: "center",
          //                 justifyContent: "center",
          //                 fontSize: "18px",
          //                 color: "#34495e",
          //                 width: "40px",
          //                 height: "40px",
          //               }}
          //             >
          //               ...
          //             </li>
          //           )}
          //           <PaginationItem>
          //             <PaginationLink
          //               onClick={() => onPageChange(totalPages - 1)}
          //               style={{
          //                 borderColor: "#34495e",
          //                 color: "#34495e",
          //                 borderRadius: "8px",
          //                 padding: "8px 12px",
          //                 width: "40px",
          //                 textAlign: "center",
          //               }}
          //             >
          //               {totalPages}
          //             </PaginationLink>
          //           </PaginationItem>
          //         </>
          //       )}
          //     </>
          //   )}

          //   {/* Next Button */}
          //   <PaginationItem disabled={currentPage + 1 === totalPages}>
          //     <PaginationLink
          //       next
          //       onClick={() => onPageChange(currentPage + 1)}
          //       style={{
          //         color: "#34495e",
          //         backgroundColor:
          //           currentPage + 1 === totalPages ? "#bdc3c7" : "white",
          //         borderRadius: "8px",
          //         padding: "8px 12px",
          //         width: "40px",
          //         textAlign: "center",
          //       }}
          //     >
          //       <FaArrowRight />
          //     </PaginationLink>
          //   </PaginationItem>
          // </Pagination>
          <Pagination
            totalPages={totalPages}
            currentPage={currentPage}
            onPageChange={onPageChange}
          />
        )}
      </div>
    </div>
  );
};

export default Table;
